import React, { useRef, useEffect } from 'react';
import Spinner from '../spinner';
import { PaginationProps } from './types';
import Icon from '../icon';
import { Button } from '../button';
import { Select } from '../form/inputs/select/select-input';
import { useTranslation } from 'react-i18next';

type Props = {
  pagination?: PaginationProps | undefined;
  loading: boolean | undefined;
  onShowMore?: () => void;
  page: number;
  rowsPerPage: number;
  onPrev: () => void;
  onNext: () => void;
  onRowsPerPageChange: (value: number) => void;
  hasPrev: boolean;
  hasNext: boolean;
  totalCount: number;
};

const PaginatedTableFooter = ({
  loading,
  onShowMore,
  page,
  rowsPerPage,
  onPrev,
  onNext,
  onRowsPerPageChange,
  hasPrev,
  hasNext,
  totalCount,
}: Props) => {
  const { t } = useTranslation();
  const selectRef = useRef<HTMLDivElement>(null);
  const start = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const end = Math.min((page + 1) * rowsPerPage, Math.max(totalCount, 0));

  useEffect(() => {
    const handleDropdownPosition = () => {
      if (!selectRef.current) return;

      const selectElement = selectRef.current.querySelector('.Select-menu-outer');
      if (!selectElement) return;

      const selectInput = selectRef.current.querySelector('.Select-control');
      if (!selectInput) return;

      const inputRect = selectInput.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 120; // approximate height for 3 options

      // Check if there's enough space below
      const spaceBelow = viewportHeight - inputRect.bottom;
      const shouldShowAbove = spaceBelow < dropdownHeight;

      const selectMenu = selectElement as HTMLElement;
      if (shouldShowAbove) {
        selectMenu.style.top = 'auto';
        selectMenu.style.bottom = '100%';
        selectMenu.style.marginBottom = '2px';
        selectMenu.style.marginTop = '0';
      } else {
        selectMenu.style.top = '100%';
        selectMenu.style.bottom = 'auto';
        selectMenu.style.marginTop = '2px';
        selectMenu.style.marginBottom = '0';
      }
    };

    // Observe for dropdown opening
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          handleDropdownPosition();
        }
      });
    });

    if (selectRef.current) {
      observer.observe(selectRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="tw-border-t tw-border-border tw-rounded-b-lg tw-bg-card">
      <div className="tw-flex tw-flex-row tw-justify-end tw-py-3 tw-px-4 tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-3" ref={selectRef}>
          <span className="tw-text-sm tw-text-gray-500">{t('performance:rows_per_page')}:</span>
          <Select
            className="!tw-min-w-[70px] pagination-dropdown"
            clearable={false}
            searchable={false}
            disabled={loading}
            value={{ label: String(rowsPerPage), value: rowsPerPage }}
            options={[10, 20, 50].map((n) => ({
              label: String(n),
              value: n,
            }))}
            onChange={(opt: any) => onRowsPerPageChange(Number(opt?.value))}
          />
        </div>

        <div className="tw-flex tw-items-center tw-gap-3">
          {loading ? (
            <Spinner centered />
          ) : (
            <span>
              {start}–{end} of {totalCount || 0}
            </span>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-gap-1">
          <Button
            type="transparent"
            size="small"
            onClick={hasPrev && !loading ? onPrev : undefined}
            disabled={!hasPrev || loading}
            aria-label="Previous page"
          >
            <Icon type="chevron_left" size={20} />
          </Button>
          <Button
            type="transparent"
            size="small"
            onClick={hasNext && !loading ? onNext : onShowMore}
            disabled={!hasNext || loading}
            aria-label="Next page"
          >
            <Icon type="chevron_right" size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaginatedTableFooter;
